import type { HomeContentCard, HomeFeedResponse, HomeNewsItem } from '../../types/auth';

export const DEFAULT_NEWS_CATEGORY = 'Новости';
export type NewsKind = 'article' | 'guide';

export const NEWS_KIND_LABELS: Record<NewsKind, string> = {
  article: 'Статья',
  guide: 'Гайд',
};

export interface NewsArticle {
  slug: string;
  path: string;
  title: string;
  body: string;
  description: string;
  category: string;
  kind: NewsKind;
  imageUrl: string | null;
  seoTitle?: string;
  seoDescription?: string;
  author?: string;
  publishedAt?: string;
}

interface NewsMeta { kind: NewsKind; category: string; seoTitle: string; seoDescription: string; author: string; publishedAt: string; }
const EMPTY_META: NewsMeta = { kind: 'article', category: DEFAULT_NEWS_CATEGORY, seoTitle: '', seoDescription: '', author: '', publishedAt: '' };

const RU_TRANSLITERATION: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z',
  и: 'i', й: 'i', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r',
  с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'c', ч: 'ch', ш: 'sh', щ: 'sch',
  ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
};

export function slugifyNewsTitle(value: string): string {
  const transliterated = value.toLowerCase().split('').map(char => RU_TRANSLITERATION[char] ?? char).join('');
  return transliterated
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80) || 'material';
}

export function newsSlugFromUrl(rawUrl: string | null | undefined): string | null {
  if (!rawUrl) return null;
  try {
    const parsed = new URL(rawUrl, 'https://agapella.io');
    const match = parsed.pathname.match(/^\/(?:blog|news)\/([^/]+)\/?$/i);
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

export function parseNewsMeta(value: string | null | undefined): NewsMeta {
  const raw = value?.trim() || '';
  if (raw.startsWith('news-json::')) {
    try {
      const parsed = JSON.parse(decodeURIComponent(raw.slice('news-json::'.length)));
      return { ...EMPTY_META, ...parsed, kind: parsed.kind === 'guide' ? 'guide' : 'article', category: parsed.category || DEFAULT_NEWS_CATEGORY };
    } catch { return { ...EMPTY_META }; }
  }
  const [kindPart, ...categoryParts] = raw.split('::');
  if (categoryParts.length) {
    return {
      ...EMPTY_META,
      kind: kindPart.trim().toLowerCase() === 'guide' || kindPart.trim().toLowerCase() === 'гайд' ? 'guide' : 'article',
      category: categoryParts.join('::').trim() || DEFAULT_NEWS_CATEGORY,
    };
  }
  return { ...EMPTY_META, category: raw || DEFAULT_NEWS_CATEGORY };
}

/**
 * Лид материала: первые ~160 символов тела без разметки.
 *
 * Маркеры снимаются, потому что лид показывают там, где разметку никто не
 * разбирает — карточка на главной, список в шторке, meta description. Без
 * этого в лиде светились «## С чего начать» и «- наблюдайте».
 */
export function newsDescription(text: string, maxLength = 160): string {
  const plain = text
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')            // картинки — целиком
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')          // ссылки — остаётся подпись
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')               // заголовки
    .replace(/^\s{0,3}(?:[-*+]|\d+[.)])\s+/gm, '')    // маркеры списка
    .replace(/\*\*([^*]+)\*\*/g, '$1');               // жирный
  const normalized = plain.replace(/\s+/g, ' ').trim();
  if (normalized.length <= maxLength) return normalized;
  const slice = normalized.slice(0, Math.max(0, maxLength - 1));
  const boundary = slice.lastIndexOf(' ');
  return `${(boundary > 80 ? slice.slice(0, boundary) : slice).trim()}…`;
}

function contentNewsCards(feed: HomeFeedResponse | null | undefined): HomeContentCard[] {
  return feed?.content?.blocks?.news?.cards?.filter(card => card.title || card.text) || [];
}

function mergedNews(feed: HomeFeedResponse | null | undefined): HomeNewsItem[] {
  const seen = new Set<string>();
  const result: HomeNewsItem[] = [];
  const rows: HomeNewsItem[] = [...(feed?.news || []), ...contentNewsCards(feed)];
  rows.forEach(item => {
    if (!item.title && !item.text) return;
    const key = item.url || `${item.title || ''}|${item.text || ''}`;
    if (seen.has(key)) return;
    seen.add(key);
    result.push(item);
  });
  return result;
}

export function buildNewsArticles(feed: HomeFeedResponse | null | undefined): NewsArticle[] {
  const usedSlugs = new Map<string, number>();
  return mergedNews(feed).map(item => {
    const title = item.title?.trim() || 'Материал Agapella';
    const body = item.text?.trim() || '';
    const preferredSlug = newsSlugFromUrl(item.url) || slugifyNewsTitle(title);
    const occurrence = (usedSlugs.get(preferredSlug) || 0) + 1;
    usedSlugs.set(preferredSlug, occurrence);
    const slug = occurrence === 1 ? preferredSlug : `${preferredSlug}-${occurrence}`;
    const meta = parseNewsMeta(item.ctaLabel);
    return {
      slug,
      path: `/blog/${encodeURIComponent(slug)}`,
      title,
      body,
      description: newsDescription(body || title),
      category: meta.category,
      kind: meta.kind,
      imageUrl: item.imageUrl || item.coverUrl || null,
      seoTitle: meta.seoTitle || title,
      seoDescription: meta.seoDescription || newsDescription(body || title),
      author: meta.author,
      publishedAt: meta.publishedAt,
    };
  });
}

export const NEWS_LAYOUT_PREVIEW: NewsArticle[] = [
  {
    slug: 'kak-nayti-vnutrennyuyu-oporu', path: '/blog/kak-nayti-vnutrennyuyu-oporu', kind: 'article', category: 'Психология',
    title: 'Как найти внутреннюю опору, когда привычные ориентиры перестают работать',
    body: 'Иногда перемены приходят раньше, чем мы успеваем к ним подготовиться. В такие моменты особенно важно вернуть внимание к тому, что остаётся доступным: телу, дыханию и честному разговору с собой.\n\nРазбираемся, из чего складывается внутренняя устойчивость и почему она не означает постоянное спокойствие.',
    description: 'Разбираемся, из чего складывается внутренняя устойчивость и как поддержать себя в период перемен.', imageUrl: null,
  },
  {
    slug: 'vechernyaya-praktika', path: '/blog/vechernyaya-praktika', kind: 'guide', category: 'Практики',
    title: 'Вечерняя практика: 7 минут для спокойного сна',
    body: 'Короткая последовательность, которую можно выполнить прямо в кровати.\n\n1. Уберите телефон.\n\n2. Сделайте пять медленных выдохов.\n\n3. Переведите внимание от стоп к макушке.',
    description: 'Простой пошаговый ритуал, который помогает завершить день и настроиться на спокойный сон.', imageUrl: null,
  },
  {
    slug: 'razgovor-s-masterom', path: '/blog/razgovor-s-masterom', kind: 'article', category: 'Мастера',
    title: '«Практика начинается с любопытства»: разговор с мастером Agapella',
    body: 'О пути к преподаванию, внимании к ученику и о том, почему универсальной практики не существует.',
    description: 'О пути к преподаванию, внимании к ученику и практике без стремления к идеальному результату.', imageUrl: null,
  },
  {
    slug: 'dyhanie-pered-vstrechey', path: '/blog/dyhanie-pered-vstrechey', kind: 'guide', category: 'Дыхание',
    title: 'Дыхание перед важной встречей', body: 'Три коротких шага, чтобы вернуть ясность и присутствие.',
    description: 'Три коротких шага, чтобы вернуть ясность и присутствие.', imageUrl: null,
  },
  {
    slug: 'novosti-soobschestva', path: '/blog/novosti-soobschestva', kind: 'article', category: 'Сообщество',
    title: 'Что нового в сообществе', body: 'Новые мастера, открытые встречи и ближайшие события Agapella.',
    description: 'Коротко о новых мастерах, открытых встречах и ближайших событиях сообщества.', imageUrl: null,
  },
];

export function newsCategories(articles: NewsArticle[]): string[] {
  return [...new Set(articles.map(article => article.category))].sort((a, b) => a.localeCompare(b, 'ru'));
}
